import React, { useContext } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import { supabase } from '../supabase-client';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import type { OverridableStringUnion } from '@mui/types';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router';

type ResetPasswordInputs = {
  password: string;
  confirmPassword: string;
};

const ResetPassword: React.FC = () => {
  const hash = window.location.hash;
  // onAuthStateChange will registed a SIGNED_IN state at this point
  if (hash.includes('type=recovery')) {
    // user came from password recovery email
    console.log('Handle password reset manually');
  }
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    setError,
  } = useForm<ResetPasswordInputs>();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<ResetPasswordInputs> = async (formData) => {
    if (formData.password !== formData.confirmPassword) {
      setError('confirmPassword', {
        type: 'manual',
        message: 'Passwords do not match',
      });
      return;
    }

    backdrop.handleBackdropOpen();
    let message = 'Password reset successfully. Please log in.';
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    try {
      const { data, error } = await supabase.auth.updateUser({
        password: formData.password,
      });

      if (error) {
        console.error('Error resetting password:', error);
        throw error;
      }
      console.log(data);
      await authCtx.handleLogout();
      navigate('/login');
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast);
      message = errorCast.message;
      severity = 'error';
    }

    snackbar.handleSnackbarOpen(message, severity);
    backdrop.handleBackdropClose();
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      bgcolor="#f5f5f5"
      minHeight="100vh">
      <Paper elevation={3} sx={{ p: 4, width: 360 }}>
        <Typography variant="h5" mb={2} align="center">
          Reset Password
        </Typography>
        {isSubmitSuccessful && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your password has been reset successfully.
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            label="New Password"
            type="password"
            fullWidth
            margin="normal"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Minimum 8 characters' },
            })}
            error={!!errors.password}
            helperText={errors.password?.message}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            fullWidth
            margin="normal"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) =>
                value === watch('password') || 'Passwords do not match',
            })}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={isSubmitting}>
            Reset Password
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
